import { useState } from "react";
import styled, { css } from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { lightSurface2, lightTextPlaceholder } from "constants/colors";
import { HelpOutline, KeyboardArrowRight } from "@mui/icons-material";
import { BREAKPOINT_TABLET } from "constants/constants";
import { Button, Tooltip } from "@mui/material";
import { useSpring, animated } from "@react-spring/web";
import { LEGEND_WIDTH, useAheadBehindMetrics } from "../aheadBehindUtils";

export function LinearClockLegend() {
  const { translate } = useFlightTranslation();

  const [expanded, setExpanded] = useState(
    window.innerWidth > BREAKPOINT_TABLET
  );

  const springCollapseContainer = useSpring({
    width: expanded ? LEGEND_WIDTH : 32,
    paddingRight: expanded ? 32 : 16,
  });

  const springExpandedContent = useSpring({
    opacity: expanded ? 1 : 0,
    transform: `translateX(${expanded ? 0 : 4}px)`,
    delay: expanded ? 200 : 0,
  });
  const y = 16;
  const springCollapsedContent = useSpring({
    transform: `translateY(${expanded ? -y : 0}px)`,
    opacity: expanded ? 0 : 1,
    delay: expanded ? 0 : 200,
  });
  const springIcon = useSpring({
    transform: `translateY(${expanded ? y : 0}px) rotate(${
      expanded ? 180 : 0
    }deg) `,
    delay: expanded ? 0 : 200,
  });
  const { operatorPageOnlyMetrics_Shift } = useAheadBehindMetrics(null);
  return (
    <LinearClockLegendStyles
      style={springCollapseContainer}
      onClick={() => {
        setExpanded(!expanded);
      }}
      {...{ $expanded: expanded }}
    >
      <animated.div
        className="btnHelp"
        style={springExpandedContent}
        onClick={e => e.stopPropagation()}
      >
        <Tooltip
          title={
            <div
              style={{
                fontSize: 12,
                lineHeight: "20px",
                maxWidth: 210,
                textAlign: "center",
              }}
            >
              {translate("Linear_Clock_Legend_Tooltip")}
            </div>
          }
          placement="top"
        >
          <HelpOutline />
        </Tooltip>
      </animated.div>
      <animated.div
        className="legend-expanded-content"
        style={springExpandedContent}
      >
        <div className="expanded-text hour">{translate("Hour")}</div>
        {/*
        {aheadBehindShowHeadCount && (
          <div className="expanded-text headCount">
            {translate("Head_Count")}
          </div>
        )} */}
        {operatorPageOnlyMetrics_Shift.map((metric, idx) => (
          <div
            key={metric.dataDumpKey}
            style={idx === 0 ? { marginTop: 4 } : {}}
            className={`expanded-text metric-${metric.dataDumpKey}`}
          >
            {translate(metric.displayNameShort ?? metric.displayName)}
          </div>
        ))}
        <div
          className="expanded-text spacer current-spacer"
          style={{ opacity: 0 }}
        >
          {"hidden_text"}
        </div>
      </animated.div>
      <animated.div
        className="legend-collapsed-content"
        style={springCollapsedContent}
      >
        <p className="collapsed-text">Legend</p>
        <animated.div className="btnCollapsed" style={springIcon}>
          <KeyboardArrowRight />
        </animated.div>
      </animated.div>
    </LinearClockLegendStyles>
  );
}
const LinearClockLegendStyles = styled(animated(Button))<{
  $expanded: boolean;
}>`
  padding-left: 16px;
  padding-top: 0;
  padding-bottom: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  height: fit-content;
  text-transform: none;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
  background: ${lightSurface2};
  overflow: hidden;
  white-space: nowrap;
  font-weight: bold;
  * {
    font-family: "Open Sans";
  }
  color: ${lightTextPlaceholder};
  font-size: 12px;
  border-radius: 8px;
  flex-shrink: 0;
  align-self: stretch;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: start;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }
  .btnHelp {
    position: absolute;
    z-index: 1;
    top: 9px;
    right: 5px;
    height: 27px;
    width: 27px;
    .MuiSvgIcon-root {
      height: 18px;
      width: 18px;
    }
  }
  .expanded-text {
    line-height: 24px;
    display: flex;
    align-items: center;
    margin-bottom: 2px;
  }
  .hour {
    height: 37px;
  }
  .metric-totalPotentialOutput {
    height: 24px;
  }
  .metric-netOutput {
    height: 24px;
  }
  .metric-outputLossSum {
    height: 42px;
    padding-top: 12px;
    box-sizing: border-box;
  }
  .metric-teamSize {
    height: 42px;
  }
  .legend-collapsed-content {
    ${props =>
      props.$expanded
        ? css`
            pointer-events: none;
          `
        : ""}
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .collapsed-text {
      font-size: 14px;
      transform: rotate(-90deg);
    }
    .btnCollapsed {
      pointer-events: none;
      position: absolute;
      bottom: 2px;
      left: 0;
      right: 0;
      svg {
        transform: scale(0.8);
      }
    }
  }
`;
